import { supabase } from '@/integrations/supabase/client';
import {
  ClientesPorStatus,
  TempoMedio,
  DesempenhoAtendente,
  StatusConfigItem,
} from '@/types/dashboard';
import { STATUS_OS_COLORS, StatusOrdemServico } from '@/types/ordemServico';

// Types inline so the hook file can re-export them for consumers
export interface OSPorStatus {
  status: string;
  statusKey: StatusOrdemServico;
  quantidade: number;
  cor: string;
}

export interface DesempenhoPrestador {
  id: string;
  nome: string;
  osAtribuidas: number;
  osConcluidas: number;
  osRecusadas: number;
  taxaConclusao: number;
}

// Raw row types returned by fetchers
export interface ClienteStatusRow {
  status: string | null;
}

export interface ClienteFinalizadoRow {
  id: string;
  criado_em: string | null;
  ultima_interacao: string | null;
  status: string | null;
}

export interface ClienteAtendenteRow {
  id: string;
  status: string | null;
  responsavel_id: string | null;
  criado_em: string | null;
  ultima_interacao: string | null;
}

export interface OSStatusRow {
  status: string | null;
}

export interface OSPrestadorRow {
  id: string;
  status: string | null;
  prestador_id: string | null;
}

export interface ProfileRow {
  id: string;
  nome: string | null;
}

export interface PrestadorWithProfileRow {
  id: string;
  profile: { id: string; nome: string | null } | { id: string; nome: string | null }[] | null;
}

// ============================================================================
// Fetchers — Supabase-touching, throw on error
// ============================================================================

export async function fetchClientesStatus(): Promise<ClienteStatusRow[]> {
  const { data, error } = await supabase
    .from('clientes_potenciais')
    .select('status')
    .eq('arquivado', false);
  if (error) throw error;
  return data ?? [];
}

export async function fetchClientesFinalizadosDesde(dataInicio: Date): Promise<ClienteFinalizadoRow[]> {
  const { data, error } = await supabase
    .from('clientes_potenciais')
    .select('id, criado_em, ultima_interacao, status')
    .eq('status', 'finalizado')
    .gte('ultima_interacao', dataInicio.toISOString());
  if (error) throw error;
  return data ?? [];
}

export async function fetchClientesComResponsavel(): Promise<ClienteAtendenteRow[]> {
  const { data, error } = await supabase
    .from('clientes_potenciais')
    .select('id, status, responsavel_id, criado_em, ultima_interacao')
    .not('responsavel_id', 'is', null);
  if (error) throw error;
  return data ?? [];
}

export async function fetchProfilesByIds(ids: string[]): Promise<ProfileRow[]> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from('profiles')
    .select('id, nome')
    .in('id', ids);
  if (error) throw error;
  return data ?? [];
}

export async function fetchOSStatus(): Promise<OSStatusRow[]> {
  const { data, error } = await supabase
    .from('ordens_servico')
    .select('status');
  if (error) throw error;
  return data ?? [];
}

export async function fetchOSComPrestador(): Promise<OSPrestadorRow[]> {
  const { data, error } = await supabase
    .from('ordens_servico')
    .select('id, status, prestador_id')
    .not('prestador_id', 'is', null);
  if (error) throw error;
  return data ?? [];
}

export async function fetchPrestadoresByIds(ids: string[]): Promise<PrestadorWithProfileRow[]> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from('prestadores_servico')
    .select('id, profile:profiles(id, nome)')
    .in('id', ids);
  if (error) throw error;
  return data ?? [];
}

// ============================================================================
// Aggregators — pure, no side effects
// ============================================================================

export function agruparClientesPorStatus(
  clientes: ClienteStatusRow[],
  statusConfig: StatusConfigItem[],
): ClientesPorStatus[] {
  const contagem: Record<string, number> = {};
  clientes.forEach(c => {
    if (c.status) {
      contagem[c.status] = (contagem[c.status] || 0) + 1;
    }
  });

  return statusConfig.map(config => ({
    status: config.label,
    statusKey: config.key,
    quantidade: contagem[config.key] || 0,
    cor: config.cor,
  }));
}

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;

export function calcularTempoMedioPorDiaSemana(clientes: ClienteFinalizadoRow[]): TempoMedio[] {
  const porDia: Record<string, { soma: number; count: number }> = {};
  DIAS_SEMANA.forEach(dia => {
    porDia[dia] = { soma: 0, count: 0 };
  });

  clientes.forEach(c => {
    if (c.criado_em && c.ultima_interacao) {
      const inicio = new Date(c.criado_em);
      const fim = new Date(c.ultima_interacao);
      const horasAtendimento = (fim.getTime() - inicio.getTime()) / (1000 * 60 * 60);
      const diaSemana = DIAS_SEMANA[fim.getDay()];
      porDia[diaSemana].soma += horasAtendimento;
      porDia[diaSemana].count++;
    }
  });

  return DIAS_SEMANA.map(dia => ({
    dia,
    tempoMedio: porDia[dia].count > 0
      ? Math.round((porDia[dia].soma / porDia[dia].count) * 10) / 10
      : 0,
  }));
}

export function agruparDesempenhoAtendentes(
  clientes: ClienteAtendenteRow[],
  profiles: ProfileRow[],
  filterByProfileId?: string,
): DesempenhoAtendente[] {
  const nomesMap: Record<string, string> = {};
  profiles.forEach(p => {
    nomesMap[p.id] = p.nome || 'Sem nome';
  });

  const porAtendente: Record<string, DesempenhoAtendente> = {};

  clientes.forEach(c => {
    if (!c.responsavel_id) return;
    const id = c.responsavel_id;
    const nome = nomesMap[id] || 'Desconhecido';

    if (!porAtendente[id]) {
      porAtendente[id] = {
        id,
        nome,
        atendidos: 0,
        finalizados: 0,
        perdidos: 0,
        tempoMedio: 0,
        emAndamento: 0,
      };
    }

    porAtendente[id].atendidos++;
    if (c.status === 'finalizado') porAtendente[id].finalizados++;
    if (c.status === 'perdido') porAtendente[id].perdidos++;
    if (!['finalizado', 'perdido'].includes(c.status || '')) {
      porAtendente[id].emAndamento++;
    }
  });

  clientes.forEach(c => {
    if (c.status === 'finalizado' && c.criado_em && c.ultima_interacao && c.responsavel_id) {
      const inicio = new Date(c.criado_em);
      const fim = new Date(c.ultima_interacao);
      const horasAtendimento = (fim.getTime() - inicio.getTime()) / (1000 * 60 * 60);
      const atendente = porAtendente[c.responsavel_id];
      if (atendente) {
        const n = atendente.finalizados;
        atendente.tempoMedio = Math.round(((atendente.tempoMedio * (n - 1) + horasAtendimento) / n) * 10) / 10;
      }
    }
  });

  let resultado = Object.values(porAtendente);
  if (filterByProfileId) {
    resultado = resultado.filter(a => a.id === filterByProfileId);
  }
  resultado.sort((a, b) => b.atendidos - a.atendidos);
  return resultado;
}

export function agruparOSPorStatus(
  ordens: OSStatusRow[],
  statusLabels: Record<StatusOrdemServico, string>,
): OSPorStatus[] {
  const contagem: Record<string, number> = {};
  ordens.forEach(os => {
    if (os.status) {
      contagem[os.status] = (contagem[os.status] || 0) + 1;
    }
  });

  return (Object.entries(statusLabels) as [StatusOrdemServico, string][]).map(([key, label]) => ({
    status: label,
    statusKey: key,
    quantidade: contagem[key] || 0,
    cor: STATUS_OS_COLORS[key],
  }));
}

export function agruparDesempenhoPrestadores(
  ordens: OSPrestadorRow[],
  prestadores: PrestadorWithProfileRow[],
): DesempenhoPrestador[] {
  const nomesMap: Record<string, string> = {};
  prestadores.forEach(p => {
    if (p.profile && !Array.isArray(p.profile)) {
      nomesMap[p.id] = p.profile.nome || 'Sem nome';
    }
  });

  const porPrestador: Record<string, DesempenhoPrestador> = {};

  ordens.forEach(os => {
    if (!os.prestador_id) return;
    const id = os.prestador_id;
    const nome = nomesMap[id] || 'Desconhecido';

    if (!porPrestador[id]) {
      porPrestador[id] = {
        id,
        nome,
        osAtribuidas: 0,
        osConcluidas: 0,
        osRecusadas: 0,
        taxaConclusao: 0,
      };
    }

    porPrestador[id].osAtribuidas++;
    if (os.status === 'concluida') porPrestador[id].osConcluidas++;
    if (os.status === 'recusada') porPrestador[id].osRecusadas++;
  });

  Object.values(porPrestador).forEach(p => {
    p.taxaConclusao = p.osAtribuidas > 0
      ? Math.round((p.osConcluidas / p.osAtribuidas) * 100)
      : 0;
  });

  const resultado = Object.values(porPrestador);
  resultado.sort((a, b) => b.osAtribuidas - a.osAtribuidas);
  return resultado;
}
